import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAuth } from '@/lib/auth/AuthContext';
import { getMsal, LOGIN_REQUEST } from '../lib/msal';
import { useSocialProviders } from '../api/social-auth';
import { directoryErrorMessage, useDirectorySignIn } from '../api/directory-auth';

/**
 * Where the Microsoft My Apps tile lands.
 *
 * OIDC has no IdP-initiated flow, so a tile cannot post us an assertion the way
 * SAML would. What Entra does instead is open this URL, which then starts an
 * ordinary sign-in. Because the person already has an Entra session — they just
 * clicked a tile inside it — Entra answers without prompting and redirects
 * straight back. The visible result is a brief flicker and then the dashboard,
 * which is the "auto login" the tile is expected to provide.
 *
 * The redirect flow is used rather than `ssoSilent`. Silent SSO runs in a hidden
 * iframe and depends on third-party cookies, which Safari blocks outright and
 * Chrome is retiring, so it fails exactly where clinicians are most likely to
 * be. A full-page redirect has no such dependency.
 */
export function MicrosoftSignInPage() {
  const navigate = useNavigate();
  const { data: providers, isLoading: providersLoading } = useSocialProviders();
  const directorySignIn = useDirectorySignIn();
  const { loginWithTokens } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Strict mode mounts effects twice in development. Without this the second
  // pass starts a second redirect, and the first one's state is discarded.
  const started = useRef(false);

  const microsoft = providers?.find((p) => p.provider === 'microsoft');

  useEffect(() => {
    if (providersLoading || started.current) return;

    if (!microsoft?.client_id) {
      setError('Microsoft sign-in is not configured for this installation.');
      return;
    }

    started.current = true;

    void (async () => {
      try {
        const msal = await getMsal(microsoft.client_id);

        // Always first. On the way back from Entra this returns the result; on
        // the way in it returns null, which is the signal to start the redirect.
        const result = await msal.handleRedirectPromise();

        if (!result) {
          await msal.loginRedirect(LOGIN_REQUEST);
          return; // The page is navigating away.
        }

        if (!result.idToken) {
          setError('Microsoft did not return a sign-in token. Please try again.');
          return;
        }

        const tokens = await directorySignIn.mutateAsync({ idToken: result.idToken });
        await loginWithTokens(tokens);
        await navigate({ to: '/' });
      } catch (err: unknown) {
        // The backend's refusal code decides the message, because the four
        // reasons have different owners: an unregistered directory is Arcadia's
        // to fix, an uninvited user is the practice administrator's.
        const response = (err as { response?: { data?: { error_code?: string; message?: string } } })?.response;
        setError(
          directoryErrorMessage(
            response?.data?.error_code,
            response?.data?.message ?? 'Could not complete Microsoft sign-in.',
          ),
        );
      }
    })();
  }, [providersLoading, microsoft, directorySignIn, loginWithTokens, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {error ? (
          <>
            <h1 className="text-lg font-medium">Microsoft sign-in</h1>
            <p className="text-muted-foreground mt-3 text-sm">{error}</p>
            <button
              type="button"
              className="mt-6 text-sm underline underline-offset-4"
              onClick={() => void navigate({ to: '/login' })}
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div
              aria-hidden
              className="border-muted-foreground/30 border-t-foreground mx-auto h-6 w-6 animate-spin rounded-full border-2"
            />
            <p className="text-muted-foreground mt-4 text-sm">Signing you in with Microsoft…</p>
          </>
        )}
      </div>
    </div>
  );
}
